import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Plus, Download, Search, Upload, FileSpreadsheet, LogIn, LogOut, ScrollText, Check, AlertCircle, X, Pencil, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, ExperienceLevel, ExperienceLevelMap, EXPERIENCE_LABELS, OOORange } from '../types';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { useAdobeImsAuth } from '../auth/useAdobeImsAuth';
import { logAvailabilityChange, logExperienceLevelChange, upsertContributorRecord, deleteContributorRecord, reorderContributorRecords, upsertSolutionRecord, deleteSolutionRecord, reorderSolutionRecords, fetchInitialStateFromDatabase, mergeMapPreservingPending, type DashboardStatePayload, fetchCurrentWeekSchedule, fetchNextWeekSchedule, updateDailyAvailability, fetchAuditLog, fetchOOORanges, createOOORange, updateOOORange, deleteOOORange } from '../utils/database';
import { ContributorWeekView } from './ContributorWeekView';
import { normalizeHydratedSolutions, reindexSolutions } from '../utils/solutionOrder';
import { normalizeHydratedContributors, reindexContributors } from '../utils/contributorOrder';
import { isLongLeaveRange, calendarDaysInclusive } from '../utils/longLeave';
function formatRelativeRefreshTime(timestamp: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m ago`;
}
function stableSlugId(input: string) {
  const normalized = input.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug) {
    return slug;
  }
  let hash = 0;
  for (const char of normalized || input.trim().toLowerCase()) {
    hash = hash * 31 + char.codePointAt(0)! >>> 0;
  }
  return `id-${hash.toString(16)}`;
}
const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Qualified and Available',
  limited: 'Qualified not Available',
  unavailable: 'Not Qualified'
};
const STATUS_COLORS: Record<AvailabilityStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300'
  },
  limited: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  unavailable: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300'
  }
};
export function Dashboard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    accessLevel,
    canManageData,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const isAdmin = accessLevel === 'superadmin' || accessLevel === 'admin';
  const hasDashboardDataAccess = isSignedIn && accessLevel !== 'unregistered' && accessLevel !== 'anonymous';
  // Filter state
  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<AvailabilityStatus | null>(null);
  const [selectedExperienceFilter, setSelectedExperienceFilter] = useState<ExperienceLevel | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<Region | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevelMap>({});
  const [weekViewContributorId, setWeekViewContributorId] = useState<string | null>(null); // full-grid weekly view (name click)
  const [weekPanelContributorId, setWeekPanelContributorId] = useState<string | null>(null); // slide-over panel (icon click)
  const [isHydrating, setIsHydrating] = useState(true);
  const [dbFeedback, setDbFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const dbFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cells with a write still in flight (sent, not yet acked). Background refreshes
  // exclude these keys so they don't clobber an optimistic update mid-flight.
  const pendingAvailabilityKeys = useRef<Set<string>>(new Set());
  const pendingExperienceKeys = useRef<Set<string>>(new Set());
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [isRefreshFailing, setIsRefreshFailing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const notifyDbResult = (result: {
    success: boolean;
    error?: string;
  }) => {
    if (dbFeedbackTimer.current) clearTimeout(dbFeedbackTimer.current);
    if (result.success) {
      setDbFeedback({
        type: 'success',
        message: 'Saved'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 2000);
    } else {
      setDbFeedback({
        type: 'error',
        message: 'Save failed — please try again'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 5000);
    }
  };

  // Applies a freshly-fetched full snapshot on top of client state. Used for the initial
  // load as well as every later poll/focus/manual refresh, so all of them go through the
  // same pending-cell-preserving merge.
  const applyDatabaseState = useCallback((databaseState: DashboardStatePayload) => {
    // Normalize OOO flags against today's date so stale isOutOfOffice values are corrected.
    const todayStr = new Date().toISOString().split('T')[0];
    const rawContributors = normalizeHydratedContributors(databaseState.contributors);
    const normalizedContributors = rawContributors.map(c => {
      if (c.oooStartDate && c.oooEndDate) {
        const shouldBeOOO = c.oooStartDate <= todayStr && c.oooEndDate >= todayStr;
        if (c.isOutOfOffice !== shouldBeOOO) return {
          ...c,
          isOutOfOffice: shouldBeOOO
        };
      }
      return c;
    });
    setContributors(normalizedContributors);
    setSolutions(normalizeHydratedSolutions(databaseState.solutions));
    setAvailability(prev => mergeMapPreservingPending(databaseState.availability, prev, pendingAvailabilityKeys.current));
    setExperienceLevels(prev => mergeMapPreservingPending(databaseState.experienceLevels, prev, pendingExperienceKeys.current));
  }, []);

  // Re-fetches the full db-get-state snapshot and applies it. Called by the initial load,
  // the poll timer, visibility-regain, and the manual refresh control — see ADR 0001.
  const refreshDashboardState = useCallback(async () => {
    setIsRefreshing(true);
    const databaseState = await fetchInitialStateFromDatabase();
    if (!databaseState) {
      setIsRefreshFailing(true);
      setIsRefreshing(false);
      return;
    }
    applyDatabaseState(databaseState);
    setIsRefreshFailing(false);
    setLastRefreshedAt(Date.now());
    setIsRefreshing(false);
  }, [applyDatabaseState]);
  useEffect(() => {
    let isCancelled = false;
    if (!isAuthReady) {
      setIsHydrating(true);
      return () => {
        isCancelled = true;
      };
    }
    if (!isSignedIn) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    if (isSignedIn && !hasDashboardDataAccess) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    const loadInitialState = async () => {
      setIsHydrating(true);
      const databaseState = await fetchInitialStateFromDatabase();
      if (isCancelled) {
        return;
      }

      // Security: never fall back to sessionStorage or buildInitialState() when db-get-state
      // fails or returns 403. Client-spoofed accessLevel could otherwise load demo/cached data
      // without a successful server-authorized read.
      if (!databaseState) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
        return;
      }
      applyDatabaseState(databaseState);
      setLastRefreshedAt(Date.now());
      setIsHydrating(false);
    };
    loadInitialState();
    return () => {
      isCancelled = true;
    };
  }, [isAuthReady, isSignedIn, accessLevel, applyDatabaseState]);

  // Keeps the dashboard fresh against concurrent edits from other users: polls every 2
  // minutes while the tab is visible, pausing while hidden, and refetches immediately on
  // regaining visibility. See ADR 0001 for why this is polling rather than push.
  useEffect(() => {
    if (!hasDashboardDataAccess || isHydrating) {
      return;
    }
    const POLL_INTERVAL_MS = 2 * 60 * 1000;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        void refreshDashboardState();
      }, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshDashboardState();
        startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasDashboardDataAccess, isHydrating, refreshDashboardState]);

  // Modal States
  const [isAddICOpen, setIsAddICOpen] = useState(false);
  const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isEditContributorOpen, setIsEditContributorOpen] = useState(false);
  const [isEditSolutionOpen, setIsEditSolutionOpen] = useState(false);
  const [newICName, setNewICName] = useState('');
  const [newICEmail, setNewICEmail] = useState('');
  const [newICRegion, setNewICRegion] = useState<Region>('USA');
  const [newICNameError, setNewICNameError] = useState('');
  const [newICEmailError, setNewICEmailError] = useState('');
  const [newSolutionName, setNewSolutionName] = useState('');
  const [newSolutionNameError, setNewSolutionNameError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importError, setImportError] = useState<string>('');
  const [editingContributor, setEditingContributor] = useState<Contributor | null>(null);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [editContributorName, setEditContributorName] = useState('');
  const [editContributorRegion, setEditContributorRegion] = useState<Region>('USA');
  const [editContributorDisplay, setEditContributorDisplay] = useState(true);
  const [editSolutionName, setEditSolutionName] = useState('');
  // OOO modal state — manages the full history of OOO ranges for one contributor
  const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
  const [oooTargetContributorId, setOOOTargetContributorId] = useState<string | null>(null);
  const [oooRanges, setOOORanges] = useState<OOORange[]>([]);
  const [oooRangesLoading, setOOORangesLoading] = useState(false);
  const [oooNewStartDate, setOOONewStartDate] = useState('');
  const [oooNewEndDate, setOOONewEndDate] = useState('');
  const [oooAddingRange, setOOOAddingRange] = useState(false);
  const [oooEditingRangeId, setOOOEditingRangeId] = useState<string | null>(null);
  const [oooEditStartDate, setOOOEditStartDate] = useState('');
  const [oooEditEndDate, setOOOEditEndDate] = useState('');
  const [oooError, setOOOError] = useState('');
  const requestManagementAccess = () => {
    if (!canManageData && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return canManageData;
  };
  const requestAdminAccess = () => {
    if (!isAdmin && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return isAdmin;
  };
  const handleAuthButtonClick = () => {
    if (isSignedIn) {
      void signOut();
      return;
    }
    void signIn();
  };
  useEffect(() => {
    if (canManageData) {
      if (isAdmin) {
        return;
      }
      setIsAddICOpen(false);
      setIsAddSolutionOpen(false);
      setIsImportOpen(false);
      return;
    }
    setIsAddICOpen(false);
    setIsAddSolutionOpen(false);
    setIsImportOpen(false);
    setIsEditContributorOpen(false);
    setIsEditSolutionOpen(false);
    setIsOOOModalOpen(false);
  }, [canManageData, isAdmin]);
  // Download current data as CSV (for editing and re-importing)
  const handleDownloadCurrentData = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const rows = contributors.map(contributor => {
      const cells = solutions.map(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const level = experienceLevels[contributor.id]?.[solution.id];
        return level ? `${status}:${level}` : status;
      });
      return [contributor.name, contributor.region, ...cells].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `availability-data-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const [isExportingLog, setIsExportingLog] = useState(false);
  const handleDownloadAuditLog = async () => {
    if (!requestAdminAccess()) return;
    setIsExportingLog(true);
    try {
      const entries = await fetchAuditLog();
      if (!entries || entries.length === 0) {
        alert('No audit log entries found.');
        return;
      }

      // Collect all unique keys across all entries for CSV columns
      const keySet = new Set<string>();
      for (const entry of entries) {
        for (const key of Object.keys(entry)) keySet.add(key);
      }
      // Preferred column order; any remaining keys are appended
      const orderedKeys = ['_id', 'timestamp', 'actionType', 'actorId', 'contributorId', 'contributorName', 'region', 'solutionId', 'solutionName', 'status', 'startDate', 'endDate', 'notes'];
      const extraKeys = [...keySet].filter(k => !orderedKeys.includes(k)).sort();
      const columns = [...orderedKeys.filter(k => keySet.has(k)), ...extraKeys];
      const escape = (v: any) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvRows = [columns.join(','), ...entries.map(e => columns.map(k => escape(e[k])).join(','))];
      const blob = new Blob([csvRows.join('\n')], {
        type: 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingLog(false);
    }
  };

  // Download template CSV
  const handleDownloadTemplate = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const exampleRow = ['John Doe', 'USA', ...solutions.map((_, i) => {
      // Show varied examples
      if (i === 0) return 'available:L3';
      if (i === 1) return 'limited:L2';
      if (i === 2) return 'unavailable';
      return 'available:L1';
    })];
    const csvContent = [headers.join(','), exampleRow.join(','), ['Jane Smith', 'EMEA', ...solutions.map(() => '')].join(',')].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'availability-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  // Parse and import CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requestAdminAccess()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          setImportError('File must contain at least a header row and one data row');
          return;
        }
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const contributorIndex = headers.findIndex(h => h.toLowerCase() === 'contributor');
        const regionIndex = headers.findIndex(h => h.toLowerCase() === 'region');
        if (contributorIndex === -1 || regionIndex === -1) {
          setImportError('CSV must have "Contributor" and "Region" columns');
          return;
        }
        const solutionHeaders = headers.slice(2); // Everything after Contributor and Region
        // Create solutions from headers
        const newSolutions: Solution[] = reindexSolutions(solutionHeaders.map(name => ({
          // id: crypto.randomUUID(),
          id: stableSlugId(name.trim()),
          name: name.trim()
        })));
        // Parse data rows
        const newContributors: Contributor[] = [];
        const newAvailability: AvailabilityMap = {};
        const newExperienceLevels: ExperienceLevelMap = {};
        const validLevels = ['L1', 'L2', 'L3', 'L4'];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const name = values[contributorIndex];
          const region = values[regionIndex] as Region;
          if (!name || !REGIONS.includes(region)) continue;
          // const contributorId = crypto.randomUUID();
          const contributorId = stableSlugId(name);
          newContributors.push({
            id: contributorId,
            name,
            email: '',
            region
          });
          // Parse availability and experience level for each solution
          // Format: "status" or "status:level" (e.g., "available:L2")
          newAvailability[contributorId] = {};
          newExperienceLevels[contributorId] = {};
          solutionHeaders.forEach((_, idx) => {
            const cellValue = values[idx + 2]?.trim() || '';
            const solutionId = newSolutions[idx].id;
            const parts = cellValue.split(':').map(p => p.trim());
            const statusPart = parts[0]?.toLowerCase();
            const levelPart = parts[1]?.toUpperCase();
            // Parse status
            if (statusPart === 'available' || statusPart === 'limited' || statusPart === 'unavailable') {
              newAvailability[contributorId][solutionId] = statusPart as AvailabilityStatus;
            } else {
              newAvailability[contributorId][solutionId] = 'unavailable';
            }
            // Parse experience level
            if (levelPart && validLevels.includes(levelPart)) {
              newExperienceLevels[contributorId][solutionId] = levelPart as ExperienceLevel;
            }
          });
        }
        // Update state
        setContributors(newContributors);
        setSolutions(newSolutions);
        setAvailability(newAvailability);
        setExperienceLevels(newExperienceLevels);
        setImportError('');
        setIsImportOpen(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setImportError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parse error:', error);
      }
    };
    reader.readAsText(file);
  };
  // Helper to update lastUpdated timestamp for a contributor
  const touchContributor = (contributorId: string) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      lastUpdated: new Date().toISOString()
    } : c));
  };
  const saveContributorRecord = (contributor: Contributor) => {
    upsertContributorRecord(contributor).then(notifyDbResult);
  };
  const saveSolutionOrder = (orderedSolutions: Solution[]) => {
    if (orderedSolutions.length === 0) return;
    reorderSolutionRecords(orderedSolutions.map(solution => ({
      id: solution.id,
      displayOrder: solution.displayOrder
    }))).then(notifyDbResult);
  };
  const saveContributorOrder = (orderedContributors: Contributor[]) => {
    if (orderedContributors.length === 0) return;
    reorderContributorRecords(orderedContributors.map(contributor => ({
      id: contributor.id,
      displayOrder: contributor.displayOrder
    }))).then(notifyDbResult);
  };
  // Handlers
  const handleToggleAvailability = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);

    // The grid displays and edits base availability only (weekly overrides are
    // managed in the week view and never surface here). Cycle from base.
    const currentStatus = availability[contributorId]?.[solutionId] ?? 'unavailable';
    const statusCycle: Record<AvailabilityStatus, AvailabilityStatus> = {
      unavailable: 'limited',
      limited: 'available',
      available: 'unavailable'
    };
    const newStatus = statusCycle[currentStatus];
    setAvailability(prev => ({
      ...prev,
      [contributorId]: {
        ...(prev[contributorId] ?? {}),
        [solutionId]: newStatus
      }
    }));
    if (contributor && solution) {
      const pendingKey = `${contributorId}:${solutionId}`;
      pendingAvailabilityKeys.current.add(pendingKey);
      logAvailabilityChange(contributor, solution, newStatus, experienceLevels[contributorId]?.[solutionId]).then(result => {
        pendingAvailabilityKeys.current.delete(pendingKey);
        notifyDbResult(result);
      });
    }
    touchContributor(contributorId);
  };
  const handleCycleExperienceLevel = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const contributorLevels = experienceLevels[contributorId] || {};
    const currentLevel = contributorLevels[solutionId];
    const cycle: Record<string, ExperienceLevel | undefined> = {
      undefined: 'L1',
      L1: 'L2',
      L2: 'L3',
      L3: 'L4',
      L4: undefined as any
    };
    const newLevel = cycle[String(currentLevel)];
    setExperienceLevels(prev => {
      const nextContributorLevels = {
        ...(prev[contributorId] || {})
      };
      if (newLevel) {
        nextContributorLevels[solutionId] = newLevel;
      } else {
        delete nextContributorLevels[solutionId];
      }
      return {
        ...prev,
        [contributorId]: nextContributorLevels
      };
    });
    if (contributor && solution) {
      const currentStatus = availability[contributorId]?.[solutionId] || 'unavailable';
      const pendingKey = `${contributorId}:${solutionId}`;
      pendingExperienceKeys.current.add(pendingKey);
      logExperienceLevelChange(contributor, solution, newLevel, currentStatus).then(result => {
        pendingExperienceKeys.current.delete(pendingKey);
        notifyDbResult(result);
      });
    }
    touchContributor(contributorId);
  };
  const handleUpdateContributorRegion = (id: string, region: Region) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === id);
    const updatedContributor = contributor ? {
      ...contributor,
      region,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === id ? {
      ...c,
      region,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
  };
  const handleAddContributor = async () => {
    if (!requestAdminAccess()) return;
    let hasError = false;
    if (!newICName.trim()) {
      setNewICNameError('Name is required.');
      hasError = true;
    } else {
      setNewICNameError('');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newICEmail.trim()) {
      setNewICEmailError('Email is required.');
      hasError = true;
    } else if (!emailRegex.test(newICEmail.trim())) {
      setNewICEmailError('Enter a valid email address.');
      hasError = true;
    } else {
      setNewICEmailError('');
    }
    if (hasError) return;
    const contributorName = newICName.trim();
    const contributorId = stableSlugId(contributorName);
    if (!contributorId) {
      setNewICNameError('Unable to generate a valid ID for that name.');
      return;
    }
    if (contributors.some(contributor => contributor.id === contributorId)) {
      setNewICNameError('A contributor with that name already exists.');
      return;
    }
    const newContributor: Contributor = {
      // id: crypto.randomUUID(),
      id: contributorId,
      name: contributorName,
      email: newICEmail.trim(),
      region: newICRegion,
      lastUpdated: new Date().toISOString()
    };
    const nextContributors = reindexContributors([...contributors, newContributor]);
    const createdContributor = nextContributors.find(c => c.id === newContributor.id) || nextContributors[nextContributors.length - 1];
    const response = await upsertContributorRecord(createdContributor);
    notifyDbResult(response);
    if (!response.success) return;
    setContributors(nextContributors);
    setNewICName('');
    setNewICEmail('');
    setNewICRegion('USA');
    setNewICNameError('');
    setNewICEmailError('');
    setIsAddICOpen(false);
  };
  const handleAddSolution = () => {
    if (!requestAdminAccess()) return;
    if (!newSolutionName.trim()) {
      setNewSolutionNameError('Solution name is required.');
      return;
    }
    const solutionName = newSolutionName.trim();
    if (solutions.some(s => s.name.toLowerCase() === solutionName.toLowerCase())) {
      setNewSolutionNameError('A solution with that name already exists.');
      return;
    }
    setNewSolutionNameError('');
    const newSolution: Solution = {
      // id: crypto.randomUUID(),
      id: stableSlugId(solutionName),
      name: solutionName
    };
    const nextSolutions = reindexSolutions([...solutions, newSolution]);
    const createdSolution = nextSolutions.find(solution => solution.id === newSolution.id) || nextSolutions[nextSolutions.length - 1];
    setSolutions(nextSolutions);
    if (createdSolution) {
      upsertSolutionRecord(createdSolution).then(notifyDbResult);
    }
    setNewSolutionName('');
    setNewSolutionNameError('');
    setIsAddSolutionOpen(false);
  };
  const handleDeleteContributor = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this contributor?')) {
      const contributor = contributors.find(c => c.id === id);
      const nextContributors = reindexContributors(contributors.filter(c => c.id !== id));
      setContributors(nextContributors);
      setAvailability(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      setExperienceLevels(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      if (contributor) {
        const result = await deleteContributorRecord({
          id: contributor.id,
          name: contributor.name,
          region: contributor.region
        });
        notifyDbResult(result);
      }
      saveContributorOrder(nextContributors);
    }
  };
  const handleDeleteSolution = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this solution?')) {
      const solution = solutions.find(s => s.id === id);
      const nextSolutions = reindexSolutions(solutions.filter(s => s.id !== id));
      setSolutions(nextSolutions);
      setAvailability(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      setExperienceLevels(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      if (solution) {
        const result = await deleteSolutionRecord({
          id: solution.id,
          name: solution.name
        });
        notifyDbResult(result);
      }
      saveSolutionOrder(nextSolutions);
    }
  };
  // Status filter handler
  const handleStatusFilterClick = (status: AvailabilityStatus) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };
  // Experience level filter handler
  const handleExperienceFilterClick = (level: ExperienceLevel) => {
    setSelectedExperienceFilter(selectedExperienceFilter === level ? null : level);
  };
  // Region filter handler
  const handleRegionFilterClick = (region: Region) => {
    setSelectedRegionFilter(selectedRegionFilter === region ? null : region);
  };
  // Filter handlers
  const handleContributorClick = (contributorId: string) => {
    setWeekViewContributorId(weekViewContributorId === contributorId ? null : contributorId);
  };
  const handleCloseWeekView = () => {
    setWeekViewContributorId(null);
  };
  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolutionId(selectedSolutionId === solutionId ? null : solutionId);
  };
  const handleClearFilters = () => {
    setSelectedContributorId(null);
    setSelectedSolutionId(null);
    setSelectedStatusFilter(null);
    setSelectedExperienceFilter(null);
    setSelectedRegionFilter(null);
  };
  // Filtered views - apply search AND click filters
  const filteredContributors = contributors.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSolutions = solutions.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const hasContributorMatch = filteredContributors.length > 0;
  const hasSolutionMatch = filteredSolutions.length > 0;
  let displayContributors = searchQuery === '' ? contributors : hasContributorMatch ? filteredContributors : contributors;
  let displaySolutions = searchQuery === '' ? solutions : hasSolutionMatch ? filteredSolutions : solutions;
  // Hide contributors flagged as not displayed for viewer/editor access levels;
  // admins and superadmins always see them
  if (!isAdmin) {
    displayContributors = displayContributors.filter(c => c.display !== false);
  }
  // Apply click filters
  if (selectedContributorId) {
    displayContributors = displayContributors.filter(c => c.id === selectedContributorId);
  }
  if (selectedSolutionId) {
    displaySolutions = displaySolutions.filter(s => s.id === selectedSolutionId);
  }
  // Apply region filter
  if (selectedRegionFilter) {
    displayContributors = displayContributors.filter(c => c.region === selectedRegionFilter);
  }
  // Apply status + experience filters - supports multi-select (both can be active)
  let statusFilteredContributors = displayContributors;
  let statusFilteredSolutions = displaySolutions;
  if (selectedStatusFilter || selectedExperienceFilter) {
    const contributorIdsMatching = new Set<string>();
    const solutionIdsMatching = new Set<string>();
    displayContributors.forEach(contributor => {
      displaySolutions.forEach(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const expLevel = experienceLevels[contributor.id]?.[solution.id];
        const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
        const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
        if (statusMatch && expMatch) {
          contributorIdsMatching.add(contributor.id);
          solutionIdsMatching.add(solution.id);
        }
      });
    });
    statusFilteredContributors = displayContributors.filter(c => contributorIdsMatching.has(c.id));
    statusFilteredSolutions = displaySolutions.filter(s => solutionIdsMatching.has(s.id));
  }
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const selectedSolution = solutions.find(s => s.id === selectedSolutionId);
  const hasActiveFilters = selectedContributorId !== null || selectedSolutionId !== null || selectedStatusFilter !== null || selectedExperienceFilter !== null || selectedRegionFilter !== null;
  // Reorder handlers
  const handleReorderContributors = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newContributors = [...contributors];
    const [removed] = newContributors.splice(fromIndex, 1);
    newContributors.splice(toIndex, 0, removed);
    const reorderedContributors = reindexContributors(newContributors);
    setContributors(reorderedContributors);
    saveContributorOrder(reorderedContributors);
  };
  const handleReorderSolutions = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newSolutions = [...solutions];
    const [removed] = newSolutions.splice(fromIndex, 1);
    newSolutions.splice(toIndex, 0, removed);
    const reorderedSolutions = reindexSolutions(newSolutions);
    setSolutions(reorderedSolutions);
    saveSolutionOrder(reorderedSolutions);
  };
  // Edit handlers
  const handleEditContributor = (contributor: Contributor) => {
    if (!requestAdminAccess()) return;
    setEditingContributor(contributor);
    setEditContributorName(contributor.name);
    setEditContributorRegion(contributor.region);
    setEditContributorDisplay(contributor.display ?? true);
    setIsEditContributorOpen(true);
  };
  const handleSaveContributor = () => {
    if (!requestAdminAccess()) return;
    if (!editingContributor || !editContributorName.trim()) return;
    const updatedContributor: Contributor = {
      ...editingContributor,
      name: editContributorName.trim(),
      region: editContributorRegion,
      display: editContributorDisplay,
      lastUpdated: new Date().toISOString()
    };
    setContributors(prev => prev.map(c => c.id === editingContributor.id ? updatedContributor : c));
    saveContributorRecord(updatedContributor);
    setIsEditContributorOpen(false);
    setEditingContributor(null);
    setEditContributorName('');
  };
  const handleEditSolution = (solution: Solution) => {
    if (!requestAdminAccess()) return;
    setEditingSolution(solution);
    setEditSolutionName(solution.name);
    setIsEditSolutionOpen(true);
  };
  const handleSaveSolution = () => {
    if (!requestAdminAccess()) return;
    if (!editingSolution || !editSolutionName.trim()) return;
    const nextSolutions = reindexSolutions(solutions.map(s => s.id === editingSolution.id ? {
      ...s,
      name: editSolutionName.trim()
    } : s));
    const updatedSolution = nextSolutions.find(s => s.id === editingSolution.id);
    setSolutions(nextSolutions);
    if (updatedSolution) {
      upsertSolutionRecord(updatedSolution).then(notifyDbResult);
    }
    setIsEditSolutionOpen(false);
    setEditingSolution(null);
    setEditSolutionName('');
  };
  // Applies a contributor's derived OOO cache (returned by db-update-ooo-range)
  // back onto local contributor state, so grid/week-view badges stay in sync.
  const applyOOOCacheToContributor = (contributorId: string, cache: {
    oooStartDate: string | null;
    oooEndDate: string | null;
    isOutOfOffice: boolean;
  }) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      isOutOfOffice: cache.isOutOfOffice,
      oooStartDate: cache.oooStartDate || undefined,
      oooEndDate: cache.oooEndDate || undefined,
      lastUpdated: new Date().toISOString()
    } : c));
  };

  // OOO handler - opens the multi-range history modal for a contributor
  const handleOpenOOOModal = (contributorId: string) => {
    if (!requestManagementAccess()) return;
    setOOOTargetContributorId(contributorId);
    setOOORanges([]);
    setOOONewStartDate('');
    setOOONewEndDate('');
    setOOOEditingRangeId(null);
    setOOOError('');
    setOOOAddingRange(false);
    setIsOOOModalOpen(true);
    setOOORangesLoading(true);
    fetchOOORanges(contributorId).then(ranges => {
      setOOORanges(ranges || []);
      setOOORangesLoading(false);
    });
  };
  const handleAddOOORange = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    if (!oooNewStartDate || !oooNewEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    if (oooNewEndDate < oooNewStartDate) {
      setOOOError('End date must be on or after start date');
      return;
    }
    setOOOAddingRange(true);
    createOOORange(oooTargetContributorId, oooNewStartDate, oooNewEndDate).then(result => {
      setOOOAddingRange(false);
      if (!result.success || !result.range) {
        setOOOError(result.error || 'Failed to add OOO range');
        return;
      }
      setOOORanges(prev => [...prev, result.range as OOORange]);
      setOOONewStartDate('');
      setOOONewEndDate('');
      if (result.contributorCache) {
        applyOOOCacheToContributor(oooTargetContributorId, result.contributorCache);
      }
    });
  };
  const handleStartEditOOORange = (range: OOORange) => {
    setOOOError('');
    setOOOEditingRangeId(range.id);
    setOOOEditStartDate(range.oooStartDate);
    setOOOEditEndDate(range.oooEndDate);
  };
  const handleCancelEditOOORange = () => {
    setOOOEditingRangeId(null);
    setOOOEditStartDate('');
    setOOOEditEndDate('');
  };
  const handleSaveEditOOORange = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId || !oooEditingRangeId) return;
    setOOOError('');
    if (!oooEditStartDate || !oooEditEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    if (oooEditEndDate < oooEditStartDate) {
      setOOOError('End date must be on or after start date');
      return;
    }
    updateOOORange(oooTargetContributorId, oooEditingRangeId, oooEditStartDate, oooEditEndDate).then(result => {
      if (!result.success || !result.range) {
        setOOOError(result.error || 'Failed to update OOO range');
        return;
      }
      const updatedRange = result.range as OOORange;
      setOOORanges(prev => prev.map(r => r.id === updatedRange.id ? updatedRange : r));
      handleCancelEditOOORange();
      if (result.contributorCache) {
        applyOOOCacheToContributor(oooTargetContributorId, result.contributorCache);
      }
    });
  };
  const handleDeleteOOORange = (range: OOORange) => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    deleteOOORange(oooTargetContributorId, range.id).then(result => {
      if (!result.success) {
        setOOOError(result.error || 'Failed to delete OOO range');
        return;
      }
      setOOORanges(prev => prev.filter(r => r.id !== range.id));
      if (result.contributorCache) {
        applyOOOCacheToContributor(oooTargetContributorId, result.contributorCache);
      }
    });
  };
  // Date Availability Handlers
  // Icon click → slide-over panel
  const handleOpenWeekView = (contributorId: string) => {
    setWeekPanelContributorId(contributorId);
  };
  const handleClosePanelView = () => {
    setWeekPanelContributorId(null);
  };
  const handleUpdateDailyAvailability = async (contributorId: string, solutionId: string, date: string, status: AvailabilityStatus | null) => {
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const result = await updateDailyAvailability(contributorId, solutionId, date, status, contributor, solution);
    notifyDbResult(result);
  };
  const accessLevelLabel = accessLevel === 'superadmin' ? 'Superadmin' : accessLevel === 'admin' ? 'Admin' : accessLevel === 'editor' ? 'Editor' : accessLevel === 'viewer' ? 'Viewer' : accessLevel === 'unregistered' ? 'Unregistered' : 'Signed out';
  if (isHydrating) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-141" data-mp="2y484wjklitmo">
        {isAuthReady ? 'Loading resource data...' : 'Initializing Adobe sign-in...'}
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-142" data-mp="kz39ghcftail">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-143" data-mp="pquw4gsh2w3g">
          <div className="flex items-center gap-4" data-id="element-144" data-mp="b2rshe1wjwht">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-145" data-mp="g00b1fqyq2jl" />
            <div data-id="element-146" data-mp="18pgxlwrwip7w">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-147" data-mp="18nw2o61od49i">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-148" data-mp="ur8xjfzkj7si">
                Sign in with Adobe ID
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-149" data-mp="3ff5eb23q5hot">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-150" data-mp="3fe3r5z9fp4qq">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-151" data-mp="3jiqfdsfwg94" /> <br data-id="element-152" data-mp="3kbq5usze5hr" />
              <p data-id="element-153" data-mp="3ktvdgxmfu1t">
              1. Click Sign-In <br data-id="element-154" data-mp="3uxoh9hpf57nc" />
              2. Login using your company account <br data-id="element-155" data-mp="3uw3nvveubur2" />
              3. Choose profile : Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-156" data-mp="3pvz8y9tkz82y">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="primary" size="lg" isLoading={isAuthLoading} data-id="element-157" data-mp="mh37vzsvkcjp">
              
              <LogIn className="mr-2 h-4 w-4" data-id="element-158" data-mp="cie0j0yluahp" />
              Sign in with Adobe ID
            </Button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-159" data-mp="2xafr39eh3af0">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-160" data-mp="2t6riqoi0oasp">
          <div className="flex items-center gap-4" data-id="element-161" data-mp="18rv4aasnr61m">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-162" data-mp="1cr3x9dfvizw4" />
            <div data-id="element-163" data-mp="1migoq3xrfo1m">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-164" data-mp="1mizvcohymq0c">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-165" data-mp="1riul2oq019jt">
                Account not provisioned
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-166" data-mp="20i9dhtpq7u6y">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-167" data-mp="20f3pkhsu2yct">
              You are signed in with Adobe ID, but this account is not registered for dashboard access.
              No resource data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-168" data-mp="1ee765zhoqp09">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="outline" size="lg" isLoading={isAuthLoading} data-id="element-169" data-mp="1ndlxt29l95mc">
              
              <LogOut className="mr-2 h-4 w-4" data-id="element-170" data-mp="1ih3tg3i679aj" />
              Sign out
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-slate-50 text-slate-900 font-sans" data-id="element-171" data-mp="1sbvszcvbc67y">
      {isRefreshing && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-id="element-172" data-mp="2631z3rt3l23t">
          <Loader2 className="h-12 w-12 animate-spin text-white" data-id="element-173" data-mp="223t6wrdphcad" />
        </div>}
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm z-30" data-id="element-174" data-mp="2g3b0dk1y5dq2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-175" data-mp="2b4hkxm33ncn9">
          <div className="flex items-center gap-3" data-id="element-176" data-mp="2uoylp8jvyoai">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-12 w-auto" data-id="element-177" data-mp="2un5ffud6q631" />
            
            <div data-id="element-178" data-mp="2ppvepikci7zw">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-179" data-mp="2ppc6itkhyxz2">
                Resource Availability Dashboard
              </h1>
              <div className="flex items-center gap-2" data-id="element-180" data-mp="2bx5o10tvgewg">
                <p className="text-sm text-slate-500" data-id="element-181" data-mp="2gpyy84840ffx">
                  Track solution coverage and individual capacity
                </p>
                {dbFeedback && <div className={`flex items-center gap-1 text-xs animate-in fade-in duration-200 ${dbFeedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-182" data-mp="26znfvcnoi3pi">
                    {dbFeedback.type === 'success' ? <Check className="h-3 w-3" data-id="element-183" data-mp="1x0ogylsptmfp" /> : <AlertCircle className="h-3 w-3" data-id="element-184" data-mp="2dhig3tpfjfqg" />}
                    <span data-id="element-185" data-mp="2dewahzm3naav">{dbFeedback.message}</span>
                  </div>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-186" data-mp="23i9oh7i1ed79">
            <div className="relative hidden md:block" data-id="element-187" data-mp="23km61jjvuljs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-188" data-mp="1yiojafc9x8m3" />
              <input type="text" placeholder="Search ICs or solutions..." className="pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-189" data-mp="1uiwg6i5nfuo4" />
              
            </div>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden md:block" data-id="element-190" data-mp="1audlxm5sdr2k" />

            {isSignedIn && <div className="hidden lg:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-191" data-mp="16saagxj4amha">
                <span className={`h-2 w-2 rounded-full ${canManageData ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-192" data-mp="3ii8afrzaqlya" />
                <span className="font-medium text-slate-800" data-id="element-193" data-mp="3rgtslm0uvyyj">
                  {user?.displayName || 'Adobe user'}
                </span>
                {user?.email && <span className="text-slate-500" data-id="element-194" data-mp="86cplldx3yd">{user.email}</span>}
                <span className={`rounded-full px-2 py-0.5 font-medium ${accessLevel === 'superadmin' || accessLevel === 'admin' ? 'bg-slate-900 text-white' : accessLevel === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-195" data-mp="2ytpqbot5dyqg">
                  
                  {accessLevelLabel}
                </span>
              </div>}

            <Button onClick={handleAuthButtonClick} variant={isSignedIn ? 'outline' : 'primary'} size="sm" isLoading={isAuthLoading && !isSignedIn} data-id="element-196" data-mp="2g0lubwsgj9so">
              
              {isSignedIn ? <LogOut className="h-4 w-4 mr-2" data-id="element-197" data-mp="1zpnlrxjfknm4" /> : <LogIn className="h-4 w-4 mr-2" data-id="element-198" data-mp="1zocli7p71xzr" />}
              {isSignedIn ? 'Sign Out' : 'Sign in with Adobe ID'}
            </Button>

            {isAdmin && <>
                <Button onClick={handleDownloadCurrentData} variant="outline" size="sm" data-id="element-199" data-mp="1pq6itnvkcntu">

                  <Download className="h-4 w-4 mr-2" data-id="element-200" data-mp="1by9u7tt3sw27" />
                  Export Data
                </Button>
                <Button onClick={handleDownloadAuditLog} variant="outline" size="sm" isLoading={isExportingLog} data-id="element-201" data-mp="121x230d7iq9k">

                  <ScrollText className="h-4 w-4 mr-2" data-id="element-202" data-mp="16yx8jcdyxv17" />
                  Log
                </Button>
                {/* <Button
                 onClick={() => setIsImportOpen(true)}
                 variant="outline"
                 size="sm">
                                   <Upload className="h-4 w-4 mr-2" />
                  Import
                 </Button> */}
                <Button onClick={() => setIsAddICOpen(true)} variant="outline" size="sm" data-id="element-203" data-mp="9gqjxf7wpqs4">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-204" data-mp="jduxiqnosdxb" />
                  Add Contributor
                </Button>
                <Button onClick={() => setIsAddSolutionOpen(true)} variant="primary" size="sm" data-id="element-205" data-mp="xcum5yb78sth">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-206" data-mp="sc6y9li9os3h" />
                  Add Solution
                </Button>
              </>}
          </div>
        </div>

        <div className="flex justify-end items-center gap-3" data-id="element-207" data-mp="1255mv3drkin3">
          {hasDashboardDataAccess && <button type="button" onClick={() => void refreshDashboardState()} disabled={isRefreshing} className={`flex items-center gap-1 text-xs ${isRefreshFailing ? 'text-red-600' : 'text-slate-400 hover:text-slate-600'} disabled:opacity-60`} title="Refresh data" data-id="element-208" data-mp="1g4nerrmcwc0o">
              <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} data-id="element-209" data-mp="hrj66bin0m0g" />
              <span data-id="element-210" data-mp="mij4nl7enc52">
                {isRefreshFailing ? 'Refresh failed — retrying' : lastRefreshedAt ? `Refreshed ${formatRelativeRefreshTime(lastRefreshedAt)}` : 'Refresh'}
              </span>
            </button>}
          <span className="text-xs hover:text-blue-500" data-id="element-211" data-mp="11gbi87te5qgm"><a target="_blank" href="https://adobe-my.sharepoint.com/:x:/r/personal/dsagar_adobe_com/_layouts/15/doc2.aspx?sourcedoc=%7B430e4f3f-47f7-428f-9fb3-2c6f5f88ee7d%7D&action=edit" data-id="element-212" data-mp="11gjvi0tfexsc">Feedback</a></span><span className="text-xs ml-1 italic" data-id="element-213" data-mp="338q75qucrv6j">v2.0</span>
        </div>
        {/* Mobile Search */}
        <div className="mt-4 md:hidden relative" data-id="element-214" data-mp="379ijisyiug71">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-215" data-mp="2zp7m7unwi0iu" />
          <input type="text" placeholder="Search ICs or solutions..." className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-216" data-mp="2uyr5o1os9ys9" />
          
        </div>

        {(authError || isAuthReady && !isSignedIn || isSignedIn && !canManageData || isSignedIn && canManageData && !isAdmin) && <div className="mt-4 space-y-2" data-id="element-217" data-mp="3dnfqyor6i64k">
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-218" data-mp="39m3y8ad0d4s5">
                {authError}
              </div>}
            {isAuthReady && !isSignedIn && !authError && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-219" data-mp="1y6favhvue3qt">
                Sign in with Adobe ID to unlock
              </div>}
            {isSignedIn && !canManageData && accessLevel === 'viewer' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-220" data-mp="3lxv7duqqrck4">
                Signed in as{' '}
                <span className="font-medium" data-id="element-221" data-mp="383lruy0jq03a">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                This account has viewer access only.
              </div>}
            {isSignedIn && canManageData && !isAdmin && <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-id="element-222" data-mp="8prwnno9ieuz">
                Signed in as{' '}
                <span className="font-medium" data-id="element-223" data-mp="3vur83eaeeue4">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                
              </div>}
          </div>}
      </header>

      {/* Legend & Active Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-2" data-id="element-224" data-mp="2kh5hdoow4kqh">
        <div className="flex flex-col gap-2" data-id="element-225" data-mp="2bxiq2vb8vbqa">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-226" data-mp="2c0ocu3wei1iu">
            <div className="flex items-center gap-4 text-xs" data-id="element-227" data-mp="27wyqll377w0z">
              <span className="font-medium text-slate-600" data-id="element-228" data-mp="2lvyhkze6on0b">Status:</span>
              {/* Clickable status filters */}
              <button onClick={() => handleStatusFilterClick('available')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'available' ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-50'}`} title="Click to filter by Qualified and Available" data-id="element-229" data-mp="2gyg7v6sce3r9">
                
                <div className="h-3 w-3 rounded-full bg-green-500" data-id="element-230" data-mp="1y5ufiwgn8vbf"></div>
                <span className={selectedStatusFilter === 'available' ? 'text-green-700 font-medium' : 'text-slate-700'} data-id="element-231" data-mp="1ah2s0ie0114z">
                  
                  Qualified and Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('limited')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'limited' ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' : 'hover:bg-yellow-50'}`} title="Click to filter by Qualified not Available" data-id="element-232" data-mp="3aisuzkctnzoo">
                
                <div className="h-3 w-3 rounded-full bg-yellow-400" data-id="element-233" data-mp="2rm152s2rymbn"></div>
                <span className={selectedStatusFilter === 'limited' ? 'text-yellow-700 font-medium' : 'text-slate-700'} data-id="element-234" data-mp="2vmbjmt4mul24">
                  
                  Qualified not Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('unavailable')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'unavailable' ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-50'}`} title="Click to filter by Not Qualified" data-id="element-235" data-mp="funo9259p0lf">
                
                <div className="h-3 w-3 rounded-full bg-red-500" data-id="element-236" data-mp="c90ohnedhm8r"></div>
                <span className={selectedStatusFilter === 'unavailable' ? 'text-red-700 font-medium' : 'text-slate-700'} data-id="element-237" data-mp="794m1slbq2vb">
                  
                  Not Qualified
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-238" data-mp="3pf0vk7ms0sap" />

              <span className="font-medium text-slate-600" data-id="element-239" data-mp="3pgu11jlnl6h4">Level:</span>
              {/* Clickable experience level filters */}
              <button onClick={() => handleExperienceFilterClick('L1')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L1' ? 'bg-slate-200 ring-2 ring-slate-400 ring-offset-1' : 'hover:bg-slate-50'}`} title="Click to filter by L1 - Foundational" data-id="element-240" data-mp="1nnnp3e55uccj">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200" data-id="element-241" data-mp="2255u9hpv1oh7">
                  L1
                </span>
                <span className={selectedExperienceFilter === 'L1' ? 'text-slate-800 font-medium' : 'text-slate-700'} data-id="element-242" data-mp="1na9fsmrgk4vo">
                  
                  Foundational
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-243" data-mp="1j6tkpuv1ecl3">
                  FOUNDATIONAL

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-244" data-mp="3npayn6w8scnx">Enablement Requirements:</p>
                  <p data-id="element-245" data-mp="3iqptqu61p4t5">Intro-level enablement completed with understanding of the basics and value of the solution.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-246" data-mp="cqda39gwx9v">Tooling Expectations:</p>
                  <p data-id="element-247" data-mp="3rojmxw8ijic3">Learners can do a standard demo in a clickable and shared environment.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-248" data-mp="a81uqtc9gilb">Field Support Implications:</p>
                  <p data-id="element-249" data-mp="5a0esha4sze2">Learners are apprenticing in the field and can support 1:Many demos.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-250" data-mp="j9rkzvuepbhi">Outcome:</p>
                  <p data-id="element-251" data-mp="f9r84fmyh8ck">Learners should be able to deliver a value pitch and 101 level demo.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L2')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L2' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1' : 'hover:bg-blue-50'}`} title="Click to filter by L2 - Intermediate" data-id="element-252" data-mp="2l3qwah7j3su7">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200" data-id="element-253" data-mp="1y607i5cajq2x">
                  L2
                </span>
                <span className={selectedExperienceFilter === 'L2' ? 'text-blue-700 font-medium' : 'text-slate-700'} data-id="element-254" data-mp="15g1ejyuz78sz">
                  
                  Intermediate
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-255" data-mp="1ae36zaeakkh4">
                  INTERMEDIATE

                  <p className="font-bold mt-2" data-id="element-256" data-mp="2tcho4m1sd7wn">Enablement Requirements:</p>
                  <p data-id="element-257" data-mp="2tfdiy1ic7ggv">Deeper dives into features, customer use Requests, and common integrations.</p>

                  <p className="font-bold mt-2" data-id="element-258" data-mp="2jeutmr8077rk">Tooling Expectations:</p>
                  <p data-id="element-259" data-mp="2jgo0o7mj3u03">Learners can do an ad hoc demo in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-260" data-mp="2oepm2z7fg5eq">Field Support Implications:</p>
                  <p data-id="element-261" data-mp="3c38udeqh8i6t">Learners are supporting early-stage or low complexity single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-262" data-mp="3h1k5dssicyqm">Outcome:</p>
                  <p data-id="element-263" data-mp="32dz0pufzirgl">Learners have completed numerous customer demos that show practical application. They should be doing ad hoc demos where they’re able to go off script.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L3')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L3' ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-offset-1' : 'hover:bg-indigo-50'}`} title="Click to filter by L3 - Advanced" data-id="element-264" data-mp="3v24luiayaigy">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200" data-id="element-265" data-mp="4i7yhzdqp6hp">
                  L3
                </span>
                <span className={selectedExperienceFilter === 'L3' ? 'text-indigo-700 font-medium' : 'text-slate-700'} data-id="element-266" data-mp="3qq7l17hif8kh">
                  
                  Advanced
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-267" data-mp="3gy1rhtbm0j36">
                  ADVANCED

                  <p className="font-bold mt-2" data-id="element-268" data-mp="331eujqjnpsx9">Enablement Requirements:</p>
                  <p data-id="element-269" data-mp="1ihj7hhpyj7l1">Covers integrations with other solutions, data migration architectures, and expert-level guidance.</p>

                  <p className="font-bold mt-2" data-id="element-270" data-mp="1nfufdmwoz80m">Tooling Expectations:</p>
                  <p data-id="element-271" data-mp="1shtkb182yczo">Learners can demo product integrations and add-ons in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-272" data-mp="1whc4tvvzom1z">Field Support Implications:</p>
                  <p data-id="element-273" data-mp="zsmjtwp2fdbs">Learners are supporting all single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-274" data-mp="14qxuuar3jtvl">Outcome:</p>
                  <p data-id="element-275" data-mp="14rfnyyigprza">Learners can easily lead ad hoc demos and go off script live with a customer. Learners have a comprehensive understanding of the tool and multiple closed deals to show practical application.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L4')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L4' ? 'bg-violet-100 ring-2 ring-violet-400 ring-offset-1' : 'hover:bg-violet-50'}`} title="Click to filter by L4 - Champion" data-id="element-276" data-mp="h2emxwbylj41">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200" data-id="element-277" data-mp="bon2uuh6zl56">
                  L4
                </span>
                <span className={selectedExperienceFilter === 'L4' ? 'text-violet-700 font-medium' : 'text-slate-700'} data-id="element-278" data-mp="kot8xvak9iub">
                  
                  Champion
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-279" data-mp="ukwl7tz4mxuz">
                  CHAMPION

                  <p className="font-bold mt-2" data-id="element-280" data-mp="2rhdiwbnaebtc">Field Support Implications:</p>
                  <p data-id="element-281" data-mp="16yt8yt8xaakw">Learners can support architecture and multi-solution conversations. The most complex meetings.</p>

                  <p className="font-bold mt-2" data-id="element-282" data-mp="1bvs1569107gg">Outcome:</p>
                  <p data-id="element-283" data-mp="x8grxb0bgzji">Learners are the SME on the team for an individual product and a thought leader as the product evolves.</p>
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-284" data-mp="o78usw9je4r0" />

              <span className="font-medium text-slate-600" data-id="element-285" data-mp="o5pcz188fln4">Region:</span>
              {REGIONS.map(region => {
              const isActive = selectedRegionFilter === region;
              const colorMap: Record<Region, {
                active: string;
                hover: string;
                text: string;
                dot: string;
              }> = {
                USA: {
                  active: 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1',
                  hover: 'hover:bg-blue-50',
                  text: 'text-blue-700 font-medium',
                  dot: 'bg-blue-500'
                },
                LATAM: {
                  active: 'bg-amber-100 ring-2 ring-amber-400 ring-offset-1',
                  hover: 'hover:bg-amber-50',
                  text: 'text-amber-700 font-medium',
                  dot: 'bg-amber-500'
                },
                India: {
                  active: 'bg-emerald-100 ring-2 ring-emerald-400 ring-offset-1',
                  hover: 'hover:bg-emerald-50',
                  text: 'text-emerald-700 font-medium',
                  dot: 'bg-emerald-500'
                },
                EMEA: {
                  active: 'bg-purple-100 ring-2 ring-purple-400 ring-offset-1',
                  hover: 'hover:bg-purple-50',
                  text: 'text-purple-700 font-medium',
                  dot: 'bg-purple-500'
                }
              };
              const colors = colorMap[region];
              return <button key={region} onClick={() => handleRegionFilterClick(region)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${isActive ? colors.active : colors.hover}`} title={`Click to filter by ${region}`} data-id="element-286" data-mp="3l61iui4znvza">
                    
                    <div className={`h-2.5 w-2.5 rounded-sm ${colors.dot}`} data-id="element-287" data-mp="7yi0ymtp772y" />
                    <span className={isActive ? colors.text : 'text-slate-700'} data-id="element-288" data-mp="7wdkz6iiyhmb">
                      {region}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && <div className="flex items-center gap-2" data-id="element-289" data-mp="mph59lni39gq">
              <span className="text-xs font-medium text-slate-600" data-id="element-290" data-mp="rm7g9ukg7eva">
                Filtered:
              </span>
              {selectedStatusFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${STATUS_COLORS[selectedStatusFilter].bg} ${STATUS_COLORS[selectedStatusFilter].text}`} data-id="element-291" data-mp="1czsay2ky96z1">
              
                  {STATUS_LABELS[selectedStatusFilter]}
                  <button onClick={() => setSelectedStatusFilter(null)} className={`hover:${STATUS_COLORS[selectedStatusFilter].bg} rounded-full p-0.5`} data-id="element-292" data-mp="1h0c3styhpofd">
                
                    <X className="h-3 w-3" data-id="element-293" data-mp="p9edtxnichxl" />
                  </button>
                </span>}
              {selectedExperienceFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedExperienceFilter === 'L1' ? 'bg-slate-100 text-slate-700' : selectedExperienceFilter === 'L2' ? 'bg-blue-100 text-blue-700' : selectedExperienceFilter === 'L3' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`} data-id="element-294" data-mp="y8rr71er5qnl">
              
                  {selectedExperienceFilter} –{' '}
                  {EXPERIENCE_LABELS[selectedExperienceFilter]}
                  <button onClick={() => setSelectedExperienceFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-295" data-mp="2m0aemfs1anda">
                
                    <X className="h-3 w-3" data-id="element-296" data-mp="2d6nj8qrqnm4m" />
                  </button>
                </span>}
              {selectedRegionFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedRegionFilter === 'USA' ? 'bg-blue-100 text-blue-700' : selectedRegionFilter === 'LATAM' ? 'bg-amber-100 text-amber-700' : selectedRegionFilter === 'India' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-297" data-mp="34x1u0e4ggic2">
              
                  {selectedRegionFilter}
                  <button onClick={() => setSelectedRegionFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-298" data-mp="1kh6wa8mw0xfs">
                
                    <X className="h-3 w-3" data-id="element-299" data-mp="1sxstpz8mofrx" />
                  </button>
                </span>}
              {(selectedStatusFilter || selectedExperienceFilter || selectedRegionFilter) && [selectedStatusFilter, selectedExperienceFilter, selectedRegionFilter].filter(Boolean).length > 1 && <span className="text-[10px] text-slate-400 italic" data-id="element-300" data-mp="1a949zglvsghq">
                    (combined filters)
                  </span>}
              {selectedContributor && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" data-id="element-301" data-mp="1k4ph1t5t46yc">
                  {selectedContributor.name}
                  <button onClick={() => setSelectedContributorId(null)} className="hover:bg-blue-200 rounded-full p-0.5" data-id="element-302" data-mp="rh32jpv1jhwp">
                
                    <X className="h-3 w-3" data-id="element-303" data-mp="we1v43z23gss" />
                  </button>
                </span>}
              {selectedSolution && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" data-id="element-304" data-mp="35ob0rtv5lsnc">
                  {selectedSolution.name}
                  <button onClick={() => setSelectedSolutionId(null)} className="hover:bg-purple-200 rounded-full p-0.5" data-id="element-305" data-mp="30rucmznyfea9">
                
                    <X className="h-3 w-3" data-id="element-306" data-mp="2qsd6kh4siydr" />
                  </button>
                </span>}
              <button onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-307" data-mp="2lzaeyejkpz07">
              
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden p-4 md:p-6" data-id="element-308" data-mp="27zr77muocpof">
        {weekViewContributorId ? (() => {
        const weekContributor = contributors.find(c => c.id === weekViewContributorId);
        if (!weekContributor) return null;
        return <div className="h-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col" data-id="element-309" data-mp="1yd6shn8d5te2">
              <ContributorWeekView contributor={weekContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleCloseWeekView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(weekContributor.id, sId, date, status)} showTabs={false} data-id="element-310" data-mp="1kegpjqyv4zhs" />
            </div>;
      })() : <AvailabilityGrid contributors={statusFilteredContributors} solutions={statusFilteredSolutions} availability={availability} onToggleAvailability={handleToggleAvailability} onDeleteContributor={handleDeleteContributor} onDeleteSolution={handleDeleteSolution} onUpdateContributorRegion={handleUpdateContributorRegion} onContributorClick={handleContributorClick} onSolutionClick={handleSolutionClick} selectedContributorId={selectedContributorId} selectedSolutionId={selectedSolutionId} selectedStatusFilter={selectedStatusFilter} selectedExperienceFilter={selectedExperienceFilter} onReorderContributors={handleReorderContributors} onReorderSolutions={handleReorderSolutions} isAdminAuthenticated={canManageData} canAdministerEntities={isAdmin} canDeleteEntities={isAdmin} allContributors={contributors} allSolutions={solutions} onEditContributor={handleEditContributor} onEditSolution={handleEditSolution} onToggleOutOfOffice={handleOpenOOOModal} experienceLevels={experienceLevels} onCycleExperienceLevel={handleCycleExperienceLevel} onOpenWeekView={hasDashboardDataAccess ? handleOpenWeekView : undefined} data-id="element-311" data-mp="2v2xl1gx3h6h8" />}
      </main>

      {/* Weekly schedule slide-over panel (icon click) */}
      {weekPanelContributorId && (() => {
      const panelContributor = contributors.find(c => c.id === weekPanelContributorId);
      if (!panelContributor) return null;
      return <div className="fixed inset-y-0 right-0 z-40 flex" data-id="element-312" data-mp="ja4up9tp4tkn">
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClosePanelView} data-id="element-313" data-mp="o261whc0bu3t" />
            <div className="relative ml-auto w-full max-w-3xl bg-white shadow-xl flex flex-col border-l border-slate-200" data-id="element-314" data-mp="s3814y5xc65s">
              <ContributorWeekView contributor={panelContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleClosePanelView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(panelContributor.id, sId, date, status)} data-id="element-315" data-mp="12271zukg6xi8" />
            </div>
          </div>;
    })()}

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
      setIsImportOpen(false);
      setImportError('');
    }} title="Import Availability Data" footer={<div className="flex justify-between items-center w-full" data-id="element-317" data-mp="1h5qp8f1dqwx6">
            <div className="flex gap-2" data-id="element-318" data-mp="1h3e6hznssij2">
              <Button variant="outline" onClick={handleDownloadTemplate} size="sm" data-id="element-319" data-mp="2dnd8mw7lragq">
              
                <FileSpreadsheet className="h-4 w-4 mr-2" data-id="element-320" data-mp="28ua9m8k238t5" />
                Blank Template
              </Button>
              <Button variant="outline" onClick={handleDownloadCurrentData} size="sm" data-id="element-321" data-mp="7174nn0xyrlo">
              
                <Download className="h-4 w-4 mr-2" data-id="element-322" data-mp="3tde2nazt44h7" />
                Current Data
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} data-id="element-323" data-mp="26jnk989ozh4">
              Close
            </Button>
          </div>} data-id="element-316" data-mp="1px0p0o2zvpab">
        
        <div className="space-y-4" data-id="element-324" data-mp="3pckfwky3ovpw">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-325" data-mp="3pdlqypf49i7m">
            <h4 className="font-medium text-blue-900 text-sm mb-2" data-id="element-326" data-mp="3agcuxievbq9k">
              CSV Format Instructions
            </h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside" data-id="element-327" data-mp="3fffreegs8o5x">
              <li data-id="element-328" data-mp="18p9jiqx5m7ny">
                First column: <strong data-id="element-329" data-mp="18ozq0tcwhidi">Contributor</strong> (name)
              </li>
              <li data-id="element-330" data-mp="1dm8d98cwoclm">
                Second column: <strong data-id="element-331" data-mp="1dl6zux8f3e0q">Region</strong> (USA, LATAM, India, or
                EMEA)
              </li>
              <li data-id="element-332" data-mp="2y396iz1pc0rr">Remaining columns: Solution names</li>
              <li data-id="element-333" data-mp="2t7aaqqe1nghb">
                Cell format:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-334" data-mp="2t4ect2274rsv">status</code> or{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-335" data-mp="37wg8e9x8gyn3">status:level</code>
              </li>
              <li data-id="element-336" data-mp="3333xf2lh8qct">
                Status values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-337" data-mp="3gv1yqr8y5hyf">available</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-338" data-mp="3cui0tlgjuab8">limited</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-339" data-mp="3cwkohyu8brcs">unavailable</code>
              </li>
              <li data-id="element-340" data-mp="3qrws6vqd4s5u">
                Level values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-341" data-mp="3lvy315x7n64d">L1</code>{' '}
                (Foundational),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-342" data-mp="1wgrkngc5nvl8">L2</code>{' '}
                (Intermediate),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-343" data-mp="1rigb76prvn3j">L3</code> (Advanced),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-344" data-mp="15my64ba2z6nc">L4</code> (Champion)
              </li>
              <li data-id="element-345" data-mp="1alhv6kjz0yjx">
                Examples:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-346" data-mp="1fdj2ds2a7z33">available:L3</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-347" data-mp="1fflzsx5rkuhu">limited:L1</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-348" data-mp="1kbud050eidmd">unavailable</code>
              </li>
              <li data-id="element-349" data-mp="mqeu9e7w74n3">
                Leave cells empty to default to "unavailable" with no level
              </li>
            </ul>
          </div>

          {importError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-350" data-mp="wm00xpnwot36">
              {importError}
            </div>}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors" data-id="element-351" data-mp="11jru0n0ael8y">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" data-id="element-352" data-mp="2d0i1sakczl4o" />
            
            <label htmlFor="csv-upload" className="cursor-pointer" data-id="element-353" data-mp="3skr3tnrznlxj">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" data-id="element-354" data-mp="3nsqx9chexurz" />
              <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-355" data-mp="3iufm8yfdte86">
                Click to upload CSV file
              </p>
              <p className="text-xs text-slate-500" data-id="element-356" data-mp="3dvl47yp8ntp4">or drag and drop</p>
            </label>
          </div>

          <p className="text-xs text-slate-500" data-id="element-357" data-mp="39sogxuor79eu">
            <strong data-id="element-358" data-mp="39w3xz31zmsiw">Note:</strong> Importing will replace all current data.
            Download your current data first if you want to keep it.
          </p>
        </div>
      </Modal>

      {/* Add Contributor Modal */}
      <Modal isOpen={isAddICOpen} onClose={() => {
      setIsAddICOpen(false);
      setNewICNameError('');
      setNewICEmailError('');
    }} title="Add New Contributor" footer={<div className="flex justify-end gap-3" data-id="element-360" data-mp="2zfsj3k20b9s3">
            <Button variant="ghost" onClick={() => setIsAddICOpen(false)} data-id="element-361" data-mp="2zhlpqzcmdu03">
              Cancel
            </Button>
            <Button onClick={handleAddContributor} data-id="element-362" data-mp="2un7qumyr14qo">Add Contributor</Button>
          </div>} data-id="element-359" data-mp="1331iezgpm8za">
        
        <div className="space-y-4" data-id="element-363" data-mp="2qlcs9q87d5gl">
          <p className="text-sm text-slate-500" data-id="element-364" data-mp="2lp5tche84u85">
            Enter the name and region of the individual contributor you would
            like to add to the dashboard.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={newICName} onChange={e => {
          setNewICName(e.target.value);
          setNewICNameError('');
        }} error={newICNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-365" data-mp="3edkx27rwnq46" />

          <Input label="Email" placeholder="e.g. jane.doe@adobe.com" type="email" value={newICEmail} onChange={e => {
          setNewICEmail(e.target.value);
          setNewICEmailError('');
        }} error={newICEmailError} onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-366" data-mp="1xzus6ysyoxth" />

          <div data-id="element-367" data-mp="1su7s64i8rjt0">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-368" data-mp="1sto6qdtduda8">
              Region
            </label>
            <select value={newICRegion} onChange={e => setNewICRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-369" data-mp="1xlziqf1edpj5">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-370" data-mp="2gjikcxlj05vj">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Add Solution Modal */}
      <Modal isOpen={isAddSolutionOpen} onClose={() => {
      setIsAddSolutionOpen(false);
      setNewSolutionNameError('');
    }} title="Add New Solution" footer={<div className="flex justify-end gap-3" data-id="element-372" data-mp="968f01n5jd6o">
            <Button variant="ghost" onClick={() => setIsAddSolutionOpen(false)} data-id="element-373" data-mp="97jkc5w8wszw">
              Cancel
            </Button>
            <Button onClick={handleAddSolution} data-id="element-374" data-mp="o0wi5952xzj4">Add Solution</Button>
          </div>} data-id="element-371" data-mp="m5990lxrmfks">
        
        <div className="space-y-4" data-id="element-375" data-mp="j6ivbv4hh0q2">
          <p className="text-sm text-slate-500" data-id="element-376" data-mp="x35s9xwfrqvz">
            Enter the name of the new Adobe solution or product area.
          </p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={newSolutionName} onChange={e => {
          setNewSolutionName(e.target.value);
          setNewSolutionNameError('');
        }} error={newSolutionNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddSolution()} data-id="element-377" data-mp="s4ba8y6am6cx" />
          
        </div>
      </Modal>

      {/* Edit Contributor Modal */}
      <Modal isOpen={isEditContributorOpen} onClose={() => {
      setIsEditContributorOpen(false);
      setEditingContributor(null);
      setEditContributorName('');
    }} title="Edit Contributor" footer={<div className="flex justify-end gap-3" data-id="element-379" data-mp="1lg670iftnzok">
            <Button variant="ghost" onClick={() => {
        setIsEditContributorOpen(false);
        setEditingContributor(null);
        setEditContributorName('');
      }} data-id="element-380" data-mp="1qf0vo10h0igl">

              Cancel
            </Button>
            <Button onClick={handleSaveContributor} data-id="element-381" data-mp="1gj7g08rgg8ul">Save Changes</Button>
          </div>} data-id="element-378" data-mp="1uhdsfzx8p618">

        <div className="space-y-4" data-id="element-382" data-mp="12o4zkrozf13j">
          <p className="text-sm text-slate-500" data-id="element-383" data-mp="16qrwv84f9e84">
            Update the contributor's name and region.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={editContributorName} onChange={e => setEditContributorName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveContributor()} data-id="element-384" data-mp="st3atwymc11h" />

          <div data-id="element-385" data-mp="1w3z8lzblkz27">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-386" data-mp="1w37l7bs5m7n0">
              Region
            </label>
            <select value={editContributorRegion} onChange={e => setEditContributorRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-387" data-mp="1r9v3lllw713r">

              {REGIONS.map(region => <option key={region} value={region} data-id="element-388" data-mp="18ey7kx53gq6y">
                  {region}
                </option>)}
            </select>
          </div>

          <div className="flex items-center justify-between" data-id="element-389" data-mp="32q37bqd28ih0">
            <div data-id="element-390" data-mp="2xsb3bxz555wg">
              <label className="block text-sm font-medium text-slate-700" data-id="element-391" data-mp="2xrrwpdexy3xq">
                Display
              </label>
              <p className="text-xs text-slate-500" data-id="element-392" data-mp="35are5diudleg">
                Hide this contributor from viewers and editors. Admins will still see them.
              </p>
            </div>
            <button type="button" role="switch" aria-checked={editContributorDisplay} onClick={() => setEditContributorDisplay(prev => !prev)} className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${editContributorDisplay ? 'bg-[#EB1000]' : 'bg-slate-300'}`} data-id="element-393" data-mp="3f13yp5qnpekl">

              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editContributorDisplay ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-394" data-mp="2w9t7sokxl1sp" />

            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal isOpen={isEditSolutionOpen} onClose={() => {
      setIsEditSolutionOpen(false);
      setEditingSolution(null);
      setEditSolutionName('');
    }} title="Edit Solution" footer={<div className="flex justify-end gap-3" data-id="element-396" data-mp="2lnazx6s6adm4">
            <Button variant="ghost" onClick={() => {
        setIsEditSolutionOpen(false);
        setEditingSolution(null);
        setEditSolutionName('');
      }} data-id="element-397" data-mp="2ln18rfve60eu">
            
              Cancel
            </Button>
            <Button onClick={handleSaveSolution} data-id="element-398" data-mp="1oy3dzxnn62ho">Save Changes</Button>
          </div>} data-id="element-395" data-mp="fknstmw6qxpu">
        
        <div className="space-y-4" data-id="element-399" data-mp="22oyt3roqhftp">
          <p className="text-sm text-slate-500" data-id="element-400" data-mp="22q02lrq3pu9b">Update the solution name.</p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={editSolutionName} onChange={e => setEditSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveSolution()} data-id="element-401" data-mp="1xx6xv3umulx8" />
          
        </div>
      </Modal>

      {/* OOO Range History Modal */}
      <Modal isOpen={isOOOModalOpen} onClose={() => setIsOOOModalOpen(false)} title="Manage Out of Office" footer={<div className="flex justify-end w-full" data-id="element-403" data-mp="37tvmf6cie4de">
            <Button variant="ghost" onClick={() => setIsOOOModalOpen(false)} data-id="element-404" data-mp="3cw4gk614bch3">
              Close
            </Button>
          </div>} data-id="element-402" data-mp="3lnmtm82rpc5y">

        <div className="space-y-4" data-id="element-405" data-mp="4d58m5aoqn15">
          {(() => {
          const contributor = contributors.find(c => c.id === oooTargetContributorId);
          return contributor ? <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-406" data-mp="3qou0m4x367bk">
                <p className="text-sm text-slate-600" data-id="element-407" data-mp="3vnnj6br8coil">
                  Out of Office ranges for{' '}
                  <span className="font-semibold text-slate-900" data-id="element-408" data-mp="2f9duthmzmzrr">
                    {contributor.name}
                  </span>
                </p>
              </div> : null;
        })()}

          <p className="text-sm text-slate-500" data-id="element-409" data-mp="3brvyxw7xhxu6">
            Add any number of out-of-office ranges. Ranges longer than 25 days
            are flagged as Long Leave. Past ranges are locked and can no longer
            be edited or deleted.
          </p>

          {oooRangesLoading && <p className="text-sm text-slate-400" data-id="element-410" data-mp="2wyj0qrv6mpaf">Loading…</p>}

          {!oooRangesLoading && <div className="space-y-2 max-h-64 overflow-y-auto" data-id="element-411" data-mp="3umj8h72w4jq1">
              {oooRanges.length === 0 && <p className="text-sm text-slate-400" data-id="element-412" data-mp="3pnpvrgvfgwqx">No OOO ranges yet.</p>}
              {[...oooRanges].sort((a, b) => a.oooStartDate.localeCompare(b.oooStartDate)).map(range => {
            const today = new Date().toISOString().split('T')[0];
            const isPast = range.oooEndDate < today;
            const isActive = range.oooStartDate <= today && range.oooEndDate >= today;
            const isEditing = oooEditingRangeId === range.id;
            return <div key={range.id} className={`p-3 rounded-md border text-sm ${isPast ? 'bg-slate-50 border-slate-200 text-slate-400' : isActive ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`} data-id="element-413" data-mp="oob4z5yey36k">

                    {isEditing ? <div className="space-y-2" data-id="element-414" data-mp="13lk7mvt62tdl">
                        <div className="grid grid-cols-2 gap-3" data-id="element-415" data-mp="17np21wrfvu1a">
                          <Input type="date" label="Start Date" value={oooEditStartDate} onChange={e => setOOOEditStartDate(e.target.value)} data-id="element-416" data-mp="17m49udslkn6l" />

                          <Input type="date" label="End Date" value={oooEditEndDate} onChange={e => setOOOEditEndDate(e.target.value)} min={oooEditStartDate || undefined} data-id="element-417" data-mp="1mdd10rc5eglu" />

                        </div>
                        <div className="flex gap-2 justify-end" data-id="element-418" data-mp="2y48dq99jy4ff">
                          <Button variant="ghost" size="sm" onClick={handleCancelEditOOORange} data-id="element-419" data-mp="2k3nvbe7juamg">
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveEditOOORange} data-id="element-420" data-mp="2o6ap3kns65ma">
                            Save
                          </Button>
                        </div>
                      </div> : <div className="flex items-center justify-between gap-3" data-id="element-421" data-mp="331qf9el8phde">
                        <div data-id="element-422" data-mp="32znp8uk37o8o">
                          <div className="font-medium" data-id="element-423" data-mp="3733m9s02jqlw">
                            {new Date(range.oooStartDate).toLocaleDateString()} – {new Date(range.oooEndDate).toLocaleDateString()}
                          </div>
                          <div className="flex gap-2 mt-1 text-[11px]" data-id="element-424" data-mp="3eicg1fonmeb">
                            {range.isLongLeave && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full border border-purple-200 font-semibold" data-id="element-425" data-mp="1o3cngimdupjp">
                                Long Leave
                              </span>}
                            {isActive && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full border border-orange-200" data-id="element-426" data-mp="1swp1jytfrdy7">
                                Active
                              </span>}
                            {isPast && <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full border border-slate-200" data-id="element-427" data-mp="1f4il0bmdf4y8">
                                Past
                              </span>}
                            {!isPast && !isActive && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full border border-blue-200" data-id="element-428" data-mp="2lk1pacbc1rt3">
                                Upcoming
                              </span>}
                          </div>
                        </div>
                        {!isPast && <div className="flex gap-2" data-id="element-429" data-mp="32kw55yrhftnb">
                            <button onClick={() => handleStartEditOOORange(range)} className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors" title="Edit range" data-id="element-430" data-mp="32jcldy6mb0gs">

                              <Pencil className="h-3.5 w-3.5" data-id="element-431" data-mp="2ev1of641lkxs" />
                            </button>
                            <button onClick={() => handleDeleteOOORange(range)} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors" title="Delete range" data-id="element-432" data-mp="2juo7hw6jxvei">

                              <Trash2 className="h-3.5 w-3.5" data-id="element-433" data-mp="1r4fusjusc37b" />
                            </button>
                          </div>}
                      </div>}
                  </div>;
          })}
            </div>}

          <div className="pt-3 border-t border-slate-200 space-y-3" data-id="element-434" data-mp="995emdv5wzen">
            <p className="text-sm font-medium text-slate-700" data-id="element-435" data-mp="48rhhqv3n9ve">Add a new range</p>
            <div className="grid grid-cols-2 gap-4" data-id="element-436" data-mp="3vg3xw3o5rja9">
              <Input type="date" label="Start Date" value={oooNewStartDate} onChange={e => setOOONewStartDate(e.target.value)} data-id="element-437" data-mp="3vfc673xom5p9" />

              <Input type="date" label="End Date" value={oooNewEndDate} onChange={e => setOOONewEndDate(e.target.value)} min={oooNewStartDate || undefined} data-id="element-438" data-mp="3lohhzu0vkw16" />

            </div>

            {oooNewStartDate && oooNewEndDate && isLongLeaveRange(oooNewStartDate, oooNewEndDate) && <p className="text-sm font-semibold text-purple-700" data-id="element-439" data-mp="32qoeoqmcak39">
                ⏳ This will be flagged as Long Leave ({calendarDaysInclusive(oooNewStartDate, oooNewEndDate)} days)
              </p>}

            {oooError && <p className="text-sm text-red-600" data-id="element-440" data-mp="1bltjzq0lrcgy">{oooError}</p>}

            <div className="flex justify-end" data-id="element-441" data-mp="wyrz7dxai3nl">
              <Button size="sm" onClick={handleAddOOORange} isLoading={oooAddingRange} data-id="element-442" data-mp="wxyo60rasrzn">
                Add range
              </Button>
            </div>
          </div>
        </div>
      </Modal>

    </div>;
}